import { useState, useEffect } from "react";
import "./DashboardSummary.css";
import { Link } from "react-router-dom";
import { BASE_URL } from "../../pages/Config";

const SUMMARY_API = `${BASE_URL}api/reporting/dashboard-summary`;

const STATUS_COLORS = {
    Ringing: { dot: "#f59e0b", bg: "#fffbeb", text: "#b45309", bar: "#f59e0b" },
    Accepted: { dot: "#10b981", bg: "#ecfdf5", text: "#065f46", bar: "#10b981" },
    Connected: { dot: "#3b82f6", bg: "#eff6ff", text: "#1d4ed8", bar: "#3b82f6" },
    Ended: { dot: "#6b7280", bg: "#f9fafb", text: "#374151", bar: "#6b7280" },
    Missed: { dot: "#ef4444", bg: "#fef2f2", text: "#b91c1c", bar: "#ef4444" },
    "Permission popup appeared": { dot: "#8b5cf6", bg: "#f5f3ff", text: "#6d28d9", bar: "#8b5cf6" },
};

const PLATFORM_COLORS = [
    "#3b82f6", "#10b981", "#f59e0b", "#ef4444",
    "#8b5cf6", "#06b6d4", "#f97316", "#84cc16", "#ec4899", "#14b8a6"
];

function getToday() {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// function getFirstDayOfYear() {
//     return `${new Date().getFullYear()}-01-01`;
// }

function StatCard({ label, value, sub, accent }) {
    return (
        <div className="ds-stat-card" style={{ "--accent": accent }}>
            <div className="ds-stat-accent" />
            <p className="ds-stat-label">{label}</p>
            <p className="ds-stat-value">{value?.toLocaleString() ?? "—"}</p>
            {sub && <p className="ds-stat-sub">{sub}</p>}
        </div>
    );
}

function BarRow({ label, count, total, color }) {
    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
    return (
        <div className="ds-bar-row">
            <div className="ds-bar-meta">
                <span className="ds-bar-label">{label}</span>
                <span className="ds-bar-count">{count.toLocaleString()} <span className="ds-bar-pct">({pct}%)</span></span>
            </div>
            <div className="ds-bar-track">
                <div
                    className="ds-bar-fill"
                    style={{ width: `${pct}%`, background: color }}
                />
            </div>
        </div>
    );
}

export default function DashboardSummary() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [fromDate, setFromDate] = useState(getToday);
    const [toDate, setToDate] = useState(getToday);

    const fetchSummary = async (fd, td) => {
        setLoading(true);
        setError(false);
        try {
            const res = await fetch(SUMMARY_API, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ fromDate: fd || null, toDate: td || null }),
            });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const j = await res.json();
            setData(j);
        } catch {
            setError(true);
            setData(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSummary(fromDate, toDate);
    }, [fromDate, toDate]);

    const summary = data?.summary;
    const platformWise = data?.platformWise || [];
    const total = summary?.totalCalls || 0;
const consultationCount = summary?.consultationCount ?? 0;

    // Top 5 platforms for display
    const topPlatforms = [...(summary?.platformCounts || [])]
        .map(p => ({ ...p, platform: p.platform?.trim() || "Unknown" }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 6);

    return (
        <div className="ds-root">
            {/* Header */}
            <div className="ds-header">
                <div>
                    <p className="ds-eyebrow">Overview</p>
                    <h2 className="ds-title">Dashboard Summary</h2>
                </div>
                <div className="ds-date-row">
                    <div className="ds-date-group">
                        <label className="ds-date-label">From</label>
                        <input
                            className="ds-date-inp"
                            type="date"
                            value={fromDate}
                            onChange={e => setFromDate(e.target.value)}
                        />
                    </div>
                    <div className="ds-date-group">
                        <label className="ds-date-label">To</label>
                        <input
                            className="ds-date-inp"
                            type="date"
                            value={toDate}
                            onChange={e => setToDate(e.target.value)}
                        />
                    </div>
                    <button
                        className="ds-btn-refresh"
                        onClick={() => fetchSummary(fromDate, toDate)}
                    >
                        ↻ Refresh
                    </button>
                </div>
            </div>

            {error && (
                <div className="ds-warn">
                    ⚠️ Unable to load summary data. Please check the API and try again.
                </div>
            )}

            {loading ? (
                <div className="ds-loading">
                    <div className="ds-spinner" />
                    <span>Loading summary…</span>
                </div>
            ) : data ? (
                <>
                    {/* KPI strip */}
                    <div className="ds-kpi-grid">
                        <StatCard
                            label="Total Calls"
                            value={total}
                            sub={`${fromDate} → ${toDate}`}
                            accent="#3b82f6"
                        />
                        <StatCard
                            label="Connected"
                            value={summary?.statusCounts?.find(s => s.status === "Connected")?.count}
                            sub={`${total > 0 ? Math.round(((summary?.statusCounts?.find(s => s.status === "Connected")?.count || 0) / total) * 100) : 0}% of total`}
                            accent="#10b981"
                        />
                        <StatCard
                            label="Missed"
                            value={summary?.statusCounts?.find(s => s.status === "Missed")?.count}
                            sub="Unanswered calls"
                            accent="#ef4444"
                        />
                        <StatCard
                            label="Platforms Active"
                            value={topPlatforms.length}
                            sub="Distinct platforms"
                            accent="#8b5cf6"
                        />
                      <Link to="/reporting/consultation-report" style={{ textDecoration: "none" }}>
    <div className="stat-link-card">
        <StatCard
            label="Total consultations"
            value={consultationCount}
            sub={`${fromDate} → ${toDate}`}
            accent="#3b82f6"
        />
    </div>
</Link>
                    </div>

                    {/* Two column: status breakdown + platform breakdown */}
                    <div className="ds-panels">
                        {/* Status breakdown */}
                        <div className="ds-panel">
                            <div className="ds-panel-header">
                                <h3 className="ds-panel-title">Call Status Breakdown</h3>
                                <span className="ds-panel-total">{total.toLocaleString()} total</span>
                            </div>
                            <div className="ds-panel-body">
                                {(summary?.statusCounts || []).map(s => (
                                    <BarRow
                                        key={s.status}
                                        label={s.status}
                                        count={s.count}
                                        total={total}
                                        color={STATUS_COLORS[s.status]?.bar || "#94a3b8"}
                                    />
                                ))}
                            </div>
                        </div>

                        {/* Platform breakdown */}
                        <div className="ds-panel">
                            <div className="ds-panel-header">
                                <h3 className="ds-panel-title">Platform Distribution</h3>
                                <span className="ds-panel-total">{topPlatforms.length} platforms</span>
                            </div>
                            <div className="ds-panel-body">
                                {topPlatforms.map((p, i) => (
                                    <BarRow
                                        key={p.platform}
                                        label={p.platform}
                                        count={p.count}
                                        total={total}
                                        color={PLATFORM_COLORS[i % PLATFORM_COLORS.length]}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Platform-wise status table */}
                    <div className="ds-table-card">
                        <div className="ds-panel-header" style={{ padding: "14px 20px", borderBottom: "1px solid #f1f5f9" }}>
                            <h3 className="ds-panel-title">Platform-wise Status Detail</h3>
                        </div>
                        <div className="ds-pw-wrap">
                            <table className="ds-pw-table">
                                <thead>
                                    <tr>
                                        <th>Platform</th>
                                        <th>Total</th>
                                        {Object.keys(STATUS_COLORS).map(s => (
                                            <th key={s}>{s}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {platformWise.filter(p => p.platform).map(p => {
                                        const getCount = (status) =>
                                            p.statusCounts?.find(s => s.status === status)?.count || 0;
                                        return (
                                            <tr key={p.platform}>
                                                <td className="ds-pw-platform">{p.platform}</td>
                                                <td className="ds-pw-total">{p.totalCalls.toLocaleString()}</td>
                                                {Object.keys(STATUS_COLORS).map(s => {
                                                    const c = getCount(s);
                                                    return (
                                                        <td key={s}>
                                                            {c > 0 ? (
                                                                <span
                                                                    className="ds-pw-badge"
                                                                    style={{
                                                                        background: STATUS_COLORS[s].bg,
                                                                        color: STATUS_COLORS[s].text,
                                                                    }}
                                                                >
                                                                    {c}
                                                                </span>
                                                            ) : (
                                                                <span className="ds-pw-zero">—</span>
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            ) : null}
        </div>
    );
}